/** Whether an editor field is marked hidden (content kept, not shown on the public site). */
export function isEditorFieldHidden(raw: unknown): boolean {
  if (raw === true || raw === 1) return true;
  if (raw === false || raw === 0 || raw == null) return false;
  if (typeof raw !== 'string') return false;
  const v = raw.trim().toLowerCase();
  if (v === 'false' || v === '0' || v === '') return false;
  return v === 'true' || v === '1' || v === 'hide' || v === 'hidden' || v === 'off';
}

export function editorFieldHideKey(fieldKey: string): string {
  return `${fieldKey}Hide`;
}

export function setEditorFieldHidden(hidden: boolean): string {
  return hidden ? 'true' : '';
}

const FIELD_HIDE_LINE_RE = /^@([a-zA-Z][a-zA-Z0-9]*)Hide:\s*(true|1|hide|hidden|off)\s*$/i;

/** Parse `@titleHide:true` style meta line; returns field base name when hidden. */
export function parseFieldHideMetaLine(line: string): { field: string } | null {
  const m = line.trim().match(FIELD_HIDE_LINE_RE);
  if (!m) return null;
  return { field: m[1] };
}

/** Split stored block text into body + `fieldHide` flags for structured items. */
export function parseFieldHideFromBlock(block: string): { body: string; hides: Record<string, string> } {
  const lines = block.split('\n');
  const hides: Record<string, string> = {};
  const bodyLines: string[] = [];
  for (const line of lines) {
    const parsed = parseFieldHideMetaLine(line);
    if (parsed) {
      hides[editorFieldHideKey(parsed.field)] = 'true';
    } else {
      bodyLines.push(line);
    }
  }
  return { body: bodyLines.join('\n'), hides };
}

/** Append `@fieldHide:true` lines for hidden hideable keys on a structured item. */
export function appendFieldHideMeta(body: string, item: Record<string, unknown>, hideableKeys: string[]): string {
  const meta = hideableKeys
    .filter((key) => isEditorFieldHidden(item[editorFieldHideKey(key)]))
    .map((key) => `@${key}Hide:true`);
  if (!meta.length) return body;
  const trimmed = body.replace(/\n+$/, '');
  return trimmed ? `${trimmed}\n${meta.join('\n')}` : meta.join('\n');
}

/** Whether a field value should render on the public site. */
export function shouldRenderEditorField(value: unknown, hideFlag: unknown): boolean {
  if (isEditorFieldHidden(hideFlag)) return false;
  if (typeof value !== 'string') return Boolean(value);
  return value.trim().length > 0;
}

/**
 * True if at least one field would render.
 * Use before painting card/item chrome so empty or fully-hidden items leave no ghost box.
 */
export function hasVisibleEditorFields(
  ...pairs: Array<[value: unknown, hideFlag?: unknown]>
): boolean {
  return pairs.some(([value, hideFlag]) => shouldRenderEditorField(value, hideFlag));
}
